import React from 'react';
import classNames from 'classnames';

import { useConfiguration } from '../../context/configurationContext';
import { bool, number, object, string, func } from 'prop-types';
import { propTypes } from '../../util/types';
import { getMapProviderApiAccess } from '../../util/maps';
import { isMapboxGlSupported } from '../../util/mapboxSupport';
import * as mapboxMap from './MapboxMap';
import * as googleMapsMap from './GoogleMap';

import css from './Map.module.css';

export const Map = props => {
  const config = useConfiguration();
  const {
    className,
    rootClassName,
    mapRootClassName,
    title,
    address,
    center,
    obfuscatedCenter,
    zoom,
    mapsConfig,
    useStaticMap,
    onZoom,
    showFuzzyCircleInitially,
  } = props;
  const mapsConfiguration = mapsConfig || config.maps;
  const hasApiAccessForMapProvider = !!getMapProviderApiAccess(mapsConfiguration);
  const isGoogleMapsInUse = mapsConfiguration.mapProvider === 'googleMaps';
  const StaticMap = isGoogleMapsInUse ? googleMapsMap.StaticMap : mapboxMap.StaticMap;
  const DynamicMap = isGoogleMapsInUse ? googleMapsMap.DynamicMap : mapboxMap.DynamicMap;
  const isMapsLibLoaded = isGoogleMapsInUse
    ? googleMapsMap.isMapsLibLoaded
    : mapboxMap.isMapsLibLoaded;

  const classes = classNames(rootClassName || css.root, className);
  const mapClasses = mapRootClassName || css.mapRoot;

  if (mapsConfiguration.fuzzy.enabled && !obfuscatedCenter) {
    throw new Error(
      'Map: obfuscatedCenter prop is required when config.maps.fuzzy.enabled === true'
    );
  }
  if (!mapsConfiguration.fuzzy.enabled && !center) {
    throw new Error('Map: center prop is required when config.maps.fuzzy.enabled === false');
  }

  const location = mapsConfiguration.fuzzy.enabled ? obfuscatedCenter : center;
  // NOTE: this used to read `zoom || fuzzy.enabled ? defaultZoomLevel : 11`, which
  // parses as `(zoom || fuzzy.enabled) ? ... : ...` and made the `zoom` prop dead.
  // No call site passed `zoom`, so the corrected grouping is a no-op for all of
  // them (`undefined || X` === `X`) and simply lets `zoom` work when passed.
  const zoomLevel =
    zoom || (mapsConfiguration.fuzzy.enabled ? mapsConfiguration.fuzzy.defaultZoomLevel : 11);

  const canRenderStaticMap = hasApiAccessForMapProvider;
  const canRenderDynamicMap = hasApiAccessForMapProvider && isMapsLibLoaded();

  // Mapbox GL needs WebGL; headless and GPU-less browsers have none. The static map is a
  // plain image from the Static Images API, so it shows the location where the dynamic map
  // would have thrown (see util/mapboxSupport.js). Only decided once the library is loaded —
  // before that "unsupported" just means "not loaded yet".
  const webGlUnavailable = !isGoogleMapsInUse && canRenderDynamicMap && !isMapboxGlSupported();
  const shouldUseStaticMap = useStaticMap || webGlUnavailable;

  if (shouldUseStaticMap && !canRenderStaticMap) {
    return <div className={classes} />;
  }

  if (!shouldUseStaticMap && !canRenderDynamicMap) {
    return <div className={classes} />;
  }

  return shouldUseStaticMap ? (
    <StaticMap
      center={location}
      zoom={zoomLevel}
      address={address}
      title={title}
      mapsConfig={mapsConfiguration}
    />
  ) : (
    <DynamicMap
      containerClassName={classes}
      mapClassName={mapClasses}
      center={location}
      zoom={zoomLevel}
      address={address}
      mapsConfig={mapsConfiguration}
      onZoom={onZoom}
      showFuzzyCircleInitially={showFuzzyCircleInitially}
    />
  );
};

Map.defaultProps = {
  className: null,
  rootClassName: null,
  mapRootClassName: null,
  address: '',
  zoom: null,
  mapsConfig: null,
  useStaticMap: false,
  showFuzzyCircleInitially: false,
};

Map.propTypes = {
  className: string,
  rootClassName: string,
  mapRootClassName: string,
  address: string,
  center: propTypes.latlng,
  obfuscatedCenter: propTypes.latlng,
  zoom: number,
  mapsConfig: object,
  useStaticMap: bool,
  onZoom: func,

  // Dynamic maps only: render the fuzzy privacy circle from the start instead
  // of the exact-location marker. Defaults to false (existing behaviour).
  showFuzzyCircleInitially: bool,
};

export default Map;
